
import { useState, useEffect, useRef } from 'react';
import { Box, TextField, Button, MenuItem, Typography, CircularProgress, SelectChangeEvent } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { UserProfile, UserRoleCategory } from '../../types';
import { saveUserProfile, getUserProfile } from '../../services/firestoreService';

interface UserProfileFormProps {
    onProfileComplete?: () => void;
    onNext?: () => void;
    /** When true, name and email are read-only (TD SYNNEX identity). Used on /profile. */
    lockIdentityFields?: boolean;
}

const UserProfileForm = ({ onProfileComplete, onNext, lockIdentityFields = false }: UserProfileFormProps) => {
    const { user } = useAuth();
    const [profile, setProfile] = useState<Partial<UserProfile>>({
        firstName: '',
        lastName: '',
        jobTitle: '',
        phoneNumber: '',
        country: '',
        region: '',
        legalEntity: '',
        preferredLanguage: 'en',
        roleCategory: UserRoleCategory.SALES,
    });
    const [error, setError] = useState<string | null>(null);
    const [isDocumentReady, setIsDocumentReady] = useState(false);
    const [statusMessage, setStatusMessage] = useState('Finalizing account setup...');
    const [saving, setSaving] = useState(false);
    const pollingRef = useRef<number | null>(null);

    useEffect(() => {
        if (user) {
            setProfile(prev => ({ ...prev, email: user.email || '' }));

            const maxRetries = 5;
            let attempt = 0;

            const poll = async () => {
                try {
                    const userProfile = await getUserProfile(user.uid);
                    if (userProfile) {
                        setProfile(prev => ({
                            ...prev,
                            email: user.email || userProfile.email || prev.email,
                            firstName: userProfile.firstName || prev.firstName,
                            lastName: userProfile.lastName || prev.lastName,
                            jobTitle: userProfile.jobTitle || prev.jobTitle,
                            phoneNumber: userProfile.phoneNumber || prev.phoneNumber,
                            country: userProfile.country || prev.country || '',
                            region: userProfile.region || prev.region || '',
                            legalEntity: userProfile.legalEntity || prev.legalEntity || '',
                            preferredLanguage: userProfile.preferredLanguage || prev.preferredLanguage,
                            roleCategory: userProfile.roleCategory || prev.roleCategory,
                        }));

                        setIsDocumentReady(true);
                        setStatusMessage('');
                        if (pollingRef.current) {
                            clearInterval(pollingRef.current);
                        }
                    } else {
                        attempt++;
                        if (attempt >= maxRetries) {
                            setError('There was a problem setting up your account. Please refresh and try again.');
                            setStatusMessage('Error: Could not retrieve profile.');
                            if (pollingRef.current) {
                                clearInterval(pollingRef.current);
                            }
                        }
                    }
                } catch (err) {
                    console.error('Polling error:', err);
                }
            };

            poll();
            pollingRef.current = window.setInterval(poll, 2000);

            return () => {
                if (pollingRef.current) {
                    clearInterval(pollingRef.current);
                }
            };
        }
    }, [user]);

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<unknown>
    ) => {
        const { name, value } = e.target;
        setProfile({ ...profile, [name]: value });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!user || !isDocumentReady) return;

        setError(null);
        setSaving(true);
        try {
            // Country, region, and legalEntity are managed via corporate sign-in and are never user-editable.
            const dataToSave: Partial<UserProfile> = lockIdentityFields
                ? {
                      jobTitle: profile.jobTitle,
                      phoneNumber: profile.phoneNumber,
                      preferredLanguage: profile.preferredLanguage,
                      roleCategory: profile.roleCategory,
                      profileComplete: true,
                  }
                : {
                      firstName: profile.firstName,
                      lastName: profile.lastName,
                      jobTitle: profile.jobTitle,
                      phoneNumber: profile.phoneNumber,
                      preferredLanguage: profile.preferredLanguage,
                      roleCategory: profile.roleCategory,
                      profileComplete: true,
                  };

            await saveUserProfile(user.uid, dataToSave);
            if (onProfileComplete) {
                onProfileComplete();
            }
            if (onNext) {
                onNext();
            }
        } catch (err) {
            setError('Failed to save profile. Please try again later.');
            console.error('Error saving user profile:', err);
        } finally {
            setSaving(false);
        }
    };

    const identityDisabled = !isDocumentReady || lockIdentityFields;

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: lockIdentityFields ? 0 : 3 }}>
            {!lockIdentityFields && (
                <Typography variant="h5" gutterBottom>
                    Complete Your Profile
                </Typography>
            )}
            {error && (
                <Typography color="error" align="center" sx={{ mb: 1 }}>
                    {error}
                </Typography>
            )}

            {!isDocumentReady && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        my: 2,
                        p: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: '2px',
                    }}
                >
                    <CircularProgress size={20} sx={{ mr: 2 }} />
                    <Typography>{statusMessage}</Typography>
                </Box>
            )}

            {lockIdentityFields && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Name, email, and organization location details are managed by TD SYNNEX and cannot be changed here.
                </Typography>
            )}

            {lockIdentityFields && (
                <TextField
                    name="email"
                    label="Email"
                    value={profile.email || user?.email || ''}
                    fullWidth
                    margin="normal"
                    disabled
                    InputLabelProps={{ shrink: true }}
                    helperText="Identity is provided by your organization sign-in."
                />
            )}

            <TextField
                name="firstName"
                label="First Name"
                value={profile.firstName}
                onChange={handleChange}
                fullWidth
                margin="normal"
                required
                disabled={identityDisabled}
                InputLabelProps={{ shrink: !!profile.firstName }}
                helperText={lockIdentityFields ? 'Managed by TD SYNNEX' : undefined}
            />
            <TextField
                name="lastName"
                label="Last Name"
                value={profile.lastName}
                onChange={handleChange}
                fullWidth
                margin="normal"
                required
                disabled={identityDisabled}
                InputLabelProps={{ shrink: !!profile.lastName }}
                helperText={lockIdentityFields ? 'Managed by TD SYNNEX' : undefined}
            />
            <TextField
                name="jobTitle"
                label="Job Title"
                value={profile.jobTitle}
                onChange={handleChange}
                fullWidth
                margin="normal"
                required
                disabled={!isDocumentReady || saving}
                InputLabelProps={{ shrink: !!profile.jobTitle }}
            />
            <TextField
                select
                name="roleCategory"
                label="Your Role Focus"
                value={profile.roleCategory}
                onChange={handleChange}
                fullWidth
                margin="normal"
                disabled={!isDocumentReady || saving}
            >
                {Object.values(UserRoleCategory).map((cat) => (
                    <MenuItem key={cat} value={cat}>
                        {cat}
                    </MenuItem>
                ))}
            </TextField>
            <TextField
                name="phoneNumber"
                label="Phone Number"
                value={profile.phoneNumber}
                onChange={handleChange}
                fullWidth
                margin="normal"
                disabled={!isDocumentReady || saving}
                InputLabelProps={{ shrink: !!profile.phoneNumber }}
            />
            <TextField
                name="country"
                label="Country"
                value={profile.country || ''}
                fullWidth
                margin="normal"
                disabled
                InputLabelProps={{ shrink: true }}
                helperText="Managed by TD SYNNEX"
            />
            <TextField
                name="region"
                label="Region"
                value={profile.region || ''}
                fullWidth
                margin="normal"
                disabled
                InputLabelProps={{ shrink: true }}
                helperText="Managed by TD SYNNEX"
            />
            <TextField
                name="legalEntity"
                label="Legal Entity"
                value={profile.legalEntity || ''}
                fullWidth
                margin="normal"
                disabled
                InputLabelProps={{ shrink: true }}
                helperText="Managed by TD SYNNEX"
            />
            <TextField
                name="preferredLanguage"
                label="Preferred Language"
                value={profile.preferredLanguage}
                onChange={handleChange}
                fullWidth
                margin="normal"
                disabled={!isDocumentReady || saving}
            />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button type="submit" variant="contained" color="primary" disabled={!isDocumentReady || saving}>
                    {saving ? 'Saving…' : onNext ? 'Next' : 'Save Profile'}
                </Button>
            </Box>
        </Box>
    );
};

export default UserProfileForm;
